import React from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { format_thousands, round_with_spaces } from "../../../utils/functions"
import ListAltIcon from "@mui/icons-material/ListAlt"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import stats_font_sizes from "../../../configs/stats_font_sizes.json"
import theme_colors from "../../../configs/theme_colors.json"

const bar_color_1 = theme_colors.bar_1
const bar_color_2 = theme_colors.bar_2
const bar_color_3 = theme_colors.bar_3
const bar_color_4 = theme_colors.bar_4
const bar_color_5 = theme_colors.bar_5
const bar_color_6 = theme_colors.bar_6
const bar_color_7 = theme_colors.bar_7
const bar_color_8 = theme_colors.bar_8
const bar_color_9 = theme_colors.bar_9
const bar_color_10 = theme_colors.bar_10

const CustomLegendTB = () => {
  return (
    <div style={{ fontSize: 13 }}>
      <span style={{ color: bar_color_1, paddingLeft: 10 }}>■</span> Male new
      <span style={{ color: bar_color_2, paddingLeft: 10 }}>■</span> Male other
      <span style={{ color: bar_color_3, paddingLeft: 10 }}>■</span> Male
      relapse <br />
      <span style={{ color: bar_color_4, paddingLeft: 10 }}>■</span> Male
      retreatment after LTF
      <span style={{ color: bar_color_5, paddingLeft: 10 }}>■</span> Male
      retreatment after failure
      <br />
      <span style={{ color: bar_color_6, paddingLeft: 10 }}>■</span> Female new
      <span style={{ color: bar_color_7, paddingLeft: 10 }}>■</span> Female
      other
      <span style={{ color: bar_color_8, paddingLeft: 10 }}>■</span> Female
      relapse
      <br />
      <span style={{ color: bar_color_9, paddingLeft: 10 }}>■</span> Female
      retreatment after LTF
      <span style={{ color: bar_color_10, paddingLeft: 10 }}>■</span> Female
      retreatment after failure
    </div>
  )
}

function CustomTooltipTB({ payload, label, active }) {
  if (active) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          fontSize: stats_font_sizes.tooltip_font_size,
          padding: 4,
          border: "black",
          boxShadow: "4px 6px 3px rgba(0, 0, 0, 0.2)",
        }}
      >
        <p style={{ padding: 0, margin: 0, fontWeight: "bold" }}>{label}</p>
        <p style={{ padding: 0, margin: 0 }}>{`Male new: ${round_with_spaces(
          payload[0].value,
        )} people`}</p>
        <p style={{ padding: 0, margin: 0 }}>{`Male other: ${round_with_spaces(
          payload[1].value,
        )} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Male relapse: ${round_with_spaces(payload[2].value)} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Male retreatment after LTF: ${round_with_spaces(
          payload[3].value,
        )} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Male retreatment after failure: ${round_with_spaces(
          payload[4].value,
        )} people`}</p>
        <p style={{ padding: 0, margin: 0 }}>{`Female new: ${round_with_spaces(
          payload[5].value,
        )} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Female other: ${round_with_spaces(payload[6].value)} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Female relapse: ${round_with_spaces(payload[7].value)} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Female retreatment after LTF: ${round_with_spaces(
          payload[8].value,
        )} people`}</p>
        <p
          style={{ padding: 0, margin: 0 }}
        >{`Female retreatment after failure: ${round_with_spaces(
          payload[9].value,
        )} people`}</p>
      </div>
    )
  }

  return null
}

export default function TBBarChart({ stats }) {
  const { tb } = stats

  const [show_legend, set_show_legend] = React.useState(false)

  const data = tb.map(({ name, male, female }) => ({
    name,
    m_new: male.new,
    m_oth: male.other,
    m_rel: male.relapse,
    m_retL: male.retreat_ltf,
    m_retF: male.retreat_fail,
    f_new: female.new,
    f_oth: female.other,
    f_rel: female.relapse,
    f_retL: female.retreat_ltf,
    f_retF: female.retreat_fail,
  }))

  function LegendButton() {
    return (
      <div
        style={{ right: 10, position: "absolute", bottom: 50 }}
        onClick={() => {
          if (show_legend === true) {
            set_show_legend(false)
          } else {
            set_show_legend(true)
          }
        }}
      >
        {show_legend === true ? <CloseFullscreenIcon /> : <ListAltIcon />}
      </div>
    )
  }

  return (
    <div style={{ width: "95%", height: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart width={500} height={210} data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            fontSize={stats_font_sizes.y_axis_label_font_size}
          />
          <YAxis
            fontSize={stats_font_sizes.y_axis_label_font_size}
            tickFormatter={(tick) => format_thousands(tick)}
            label={{
              value: "Population",
              style: { textAnchor: "middle" },
              angle: -90,
              position: "left",
              offset: -20,
            }}
          />
          <Tooltip content={CustomTooltipTB} />
          {show_legend ? <Legend content={CustomLegendTB} /> : null}
          <Bar dataKey="m_new" stackId="a" fill={bar_color_1} />
          <Bar dataKey="m_oth" stackId="a" fill={bar_color_2} />
          <Bar dataKey="m_retL" stackId="a" fill={bar_color_3} />
          <Bar dataKey="m_rel" stackId="a" fill={bar_color_4} />
          <Bar dataKey="m_retF" stackId="a" fill={bar_color_5} />
          <Bar dataKey="f_new" stackId="b" fill={bar_color_6} />
          <Bar dataKey="f_oth" stackId="b" fill={bar_color_7} />
          <Bar dataKey="f_rel" stackId="b" fill={bar_color_8} />
          <Bar dataKey="f_retL" stackId="b" fill={bar_color_9} />
          <Bar dataKey="f_retF" stackId="b" fill={bar_color_10} />
        </BarChart>
      </ResponsiveContainer>
      <LegendButton />
    </div>
  )
}
